"use client";
import React, { useRef, useState } from "react";
import { Link, usePathname } from "@/i18n/navigation";
import Image from "next/image";

export const MenuItem = ({
  setActive,
  active,
  item,
  href,
  isActiveLink = false,
  children,
}: {
  setActive: (item: string | null) => void;
  active: string | null;
  item: string;
  href?: string;
  isActiveLink?: boolean;
  children?: React.ReactNode;
}) => {
  const pathname = usePathname();
  const hasChildren = Boolean(children);
  // consider a link active when pathname equals href or starts with href + '/'
  const computedActive =
    href && pathname
      ? pathname === href || pathname.startsWith(href + "/")
      : false;
  const finalActive = isActiveLink || computedActive;

  return (
    <div onMouseEnter={() => setActive(item)} className="relative">
      {href ? (
        <Link
          href={href}
          onClick={(event) => {
            if (!hasChildren) return;
            if (active !== item) {
              event.preventDefault();
              setActive(item);
            }
          }}
          className={`transition-all duration-200 whitespace-nowrap text-sm xl:text-base font-medium relative group hover:text-red-600 dark:hover:text-red-400 ${
            finalActive
              ? "text-red-600 dark:text-red-400"
              : "text-gray-700 dark:text-gray-300"
          }`}
        >
          {item}
          <span
            className={`absolute -bottom-1 left-0 h-0.5 bg-red-600 dark:bg-red-400 transition-all duration-300 ${
              finalActive ? "w-full" : "w-0 group-hover:w-full"
            }`}
          />
        </Link>
      ) : (
        <p className="transition-all duration-200 whitespace-nowrap text-sm xl:text-base font-medium relative group text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400">
          {item}
        </p>
      )}
      {active === item && (
        <div
          className="absolute top-[calc(100%_+_0.75rem)] left-1/2 -translate-x-1/2 pt-3 z-[70] animate-in fade-in zoom-in-95 duration-200"
          onMouseEnter={() => setActive(item)}
          onMouseLeave={() => setActive(null)}
        >
          <div className="bg-white dark:bg-black backdrop-blur-sm rounded-2xl overflow-hidden border border-black/[0.15] dark:border-white/[0.15] shadow-xl">
            <div className="w-max h-full p-4">
              {children}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const Menu = ({
  setActive,
  children,
}: {
  setActive: (item: string | null) => void;
  children: React.ReactNode;
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setActive(null), 120);
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  return (
    <nav
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="relative flex items-center space-x-6"
    >
      {children}
    </nav>
  );
};

export const ProductItem = ({
  title,
  description,
  href,
  src,
  fallbackSrc = "/static/misc/producto-estrella-fallback.webp",
}: {
  title: string;
  description?: string;
  href: string;
  src: string;
  fallbackSrc?: string;
}) => {
  const [currentSrc, setCurrentSrc] = useState(src || fallbackSrc);
  const [prevSrc, setPrevSrc] = useState(src);
  // Reset to the new src whenever the prop changes (e.g. reused instance across menu items).
  // Adjusted during render instead of an effect, to avoid an extra render pass.
  if (src !== prevSrc) {
    setPrevSrc(src);
    setCurrentSrc(src || fallbackSrc);
  }

  return (
    <Link href={href} className="flex space-x-3 w-full max-w-[420px]">
      <Image
        src={currentSrc}
        width={120}
        height={120}
        alt={title}
        className="shrink-0 rounded-lg shadow-md object-cover h-20 w-28"
        onError={() => setCurrentSrc(fallbackSrc)}
      />
      <div className="flex-1 min-w-0">
        <h4 className="text-base font-semibold mb-1 text-black dark:text-white line-clamp-2">
          {title}
        </h4>
        {description && (
          <p className="text-neutral-700 text-sm dark:text-neutral-300 line-clamp-3">
            {description}
          </p>
        )}
      </div>
    </Link>
  );
};

export const HoveredLink = ({ children, ...rest }: React.ComponentPropsWithoutRef<typeof Link>) => {
  return (
    <Link
      {...rest}
      className="text-sm xl:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-red-600 dark:hover:text-red-400 transition"
    >
      {children}
    </Link>
  );
};
